import "./App.css";
import "./styles/NotFound.css";
import NotFound from "./views/NotFound";
import AccessDenied from "./views/AccessDenied";
import ConfirmResults from "./views/ConfirmResults";
import WaitlistUnsubscribe from "./views/WaitlistUnsubscribe";
import SignupforFamilyMember from "./views/Signup-For-FamilyMember";
import SignupforMyself from "./views/Signup-For-Myself";
import Signupoptions from "./views/Signupoptions";
import Login from "./views/login";
import ProviderLogin from "./views/Provider-Login";
import FAQ from "./views/FAQ";
import Aboutus from "./views/AboutUs";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Policy from "./views/legal/policy";
import Telehealth from "./views/legal/telehealth";
import NoticeOfPrivacyPractices from "./views/legal/noticeOfPrivacyPractices";
import HipaaAuthorization from "./views/legal/hipaaAuthorization";
import Terms from "./views/legal/termsConditions";
import "react-toastify/dist/ReactToastify.css";
import { YouroToaster } from "./utils/toast";
import Home from "./views/Home";
import PricingPage from "./views/PricingPage";
import ConditionsPage from "./views/CondidtionsPage";
import ConditionDetailPage from "./views/ConditionDetailPage"
import Assessment from "./views/Assessment"
import PatientView from "./views/Patient UI/PatientView";
import DoctorHomePage from "./views/Doctor UI/Doctor-HomePage";
import DoctorPrescriptions from "./views/Doctor UI/DoctorPrescriptions";
import DoctorHelpSupport from "./views/Doctor UI/DoctorHelpSupport";
import AdminDoctorsList from "./views/Admin UI/Admin-DoctorsList";
import AdminPatientList from "./views/Admin UI/Admin-PatientList";
import DoctorProfile from "./views/Doctor UI/DoctorProfile";
import DoctorAppointments from "./views/Doctor UI/Doctor-appointment";
import ViewProfile from "./views/Doctor UI/View-Profile";
import DoctorChat from "./views/Doctor UI/Doctor-Chat";
import AdminDeniedDoctorsList from "./views/Admin UI/Admin-DeniedDoctorsList";
import AdminViewDoctorProfile from "./views/Admin UI/Admin-ViewDoctorProfile";
import AdminViewPatientProfile from "./views/Admin UI/Admin-ViewPatientProfile";
import AdminMaintainenceList from "./views/Admin UI/Admin-Maintainence";
import ForgotPassword from "./views/ForgotPassword";
import VerifyEmail from "./views/VerifyEmail";
import AdminAssistantList from "./views/Admin UI/Admin-AssistantList";
import Cookies from "js-cookie";
import DoctorAccessDenied from './views/Doctor UI/DoctorAccessDenied';
import NoteTemplate from './views/Admin UI/Admin-NoteTemplateList';
import AdminAddTemplate from './views/Admin UI/Admin-AddTemplate';

// Host and port come from the build environment so a deploy never requires a
// source edit. `npm start` falls back to localhost; `npm run build` picks up
// .env.production, which points at the AWS backend. Note the trailing colon on
// baseUrl — callers concatenate baseUrl + PORT.
export const API_DETAILS = {
  baseUrl: process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:',
  // ?? rather than ||, so an intentionally empty port is respected. Callers
  // concatenate baseUrl + PORT, and the API is now behind TLS on the default
  // port — with || an empty value fell back to the default and appended it to
  // the hostname, producing an unreachable address.
  PORT: process.env.REACT_APP_API_PORT ?? '9095',
  baseExtension: '/youro/api/v1',
};

export const USER_TYPES = {
  user: "PATIENT",
  doctor: "PROVIDER",
  admin: "ADMIN",
  assistant: "ASSITANT",
};
export const subscriptionStatus = {
  active: "ACTIVE",
  inActive: "INACTIVE",
};
export const DOCTOR_STATUS = {
  approved: "APPROVED",
  pending: "PENDING",
  denied: "DENIED",
};

export const COOKIE_KEYS = {
  token: "TOKEN",
  userId: "U_ID",
  userName: "U_NAME",
  userType: "U_TYPE",
};

// Whether this browser believes someone is signed in. The session itself is an
// HttpOnly cookie no script can see; this reads the account id kept alongside it
// for the screens. It only decides which page to show: the server checks the
// session on every request, and an expired one sends the browser back to
// sign-in (utils/authInterceptor).
const IsAuthenticated = () => Boolean(Cookies.get(COOKIE_KEYS.userId));

// Where each kind of account belongs. Every route below admits the role whose
// home it is, which is what stops a redirect ending up anywhere that would
// redirect again: an account sent to its own home is always let in.
const HOME_FOR_ROLE = {
  PATIENT: "/patient-home",
  PROVIDER: "/doctor-home",
  ADMIN: "/admin-doctors",
  // Assistants run the admin console alongside admins. The screens already know
  // about them: they show the lists and hide the row actions.
  ASSITANT: "/admin-doctors",
};

/**
 * One guard for every protected route.
 *
 * There were three, each with its own idea of where to send an account it did
 * not want, and between them they could not agree: an assistant was sent from
 * /doctor-home to /admin-doctors, which sent it back to /doctor-home, forever,
 * so an assistant could never use the app at all. Routing by a single table of
 * where each role belongs means a refused account is always sent somewhere that
 * will have it, and a role with no home of its own gets a page saying so rather
 * than a loop.
 */
const RequireRole = ({ roles, children }) => {
  if (!IsAuthenticated()) return <Navigate to="/login" replace />;

  const uType = Cookies.get(COOKIE_KEYS.userType);

  if (roles.includes(uType)) return children;

  const home = HOME_FOR_ROLE[uType];

  return <Navigate to={home ?? "/no-access"} replace />;
};

const PatientProtectedRoute = ({ children }) => (
  <RequireRole roles={["PATIENT"]}>{children}</RequireRole>
);

const DoctorProtectedRoute = ({ children }) => (
  <RequireRole roles={["PROVIDER"]}>{children}</RequireRole>
);

const AdminProtectedRoute = ({ children }) => (
  <RequireRole roles={["ADMIN", "ASSITANT"]}>{children}</RequireRole>
);

// View-Profile is a patient's chart, and it already renders an admin sidebar for
// ADMIN and ASSITANT. Its route only admitted PROVIDER, so the admin patient
// list's "View Care Plan" button bounced straight back to the doctors list and
// that branch of the component could never run. The server agrees with this
// wider rule: the chart endpoints behind the screen use
// AccessGuard.assertSelfOrClinician, which admits all three.
const ClinicianProtectedRoute = ({ children }) => (
  <RequireRole roles={["PROVIDER", "ADMIN", "ASSITANT"]}>{children}</RequireRole>
);
function App() {
  return (
    <>
      <Router>
        <Routes>
          <Route path="/" element={<Home />}></Route>
          <Route path="/conditions" element={<ConditionsPage />} />
          <Route path="/conditions/:id" element={<ConditionDetailPage />} />
          <Route path="/assessment/:name?" element={<Assessment />} />
          <Route path="/login" element={<Login />}></Route>
          <Route path="/provider-login" element={<ProviderLogin />}></Route>
          {/* Public: linked from Help & Support on both platforms, and useful to
              anyone deciding whether to sign up. */}
          <Route path="/faq" element={<FAQ />}></Route>
          <Route path="/forgot-password" element={<ForgotPassword />}></Route>
          <Route path="/verify-email/:userEmail?" element={<VerifyEmail />}></Route>
          <Route path="/signup" element={<Signupoptions />}></Route>
          {/* Older sign-up pages, linked from nowhere. They skipped the
              "where do you live?" question, so an address typed in directly
              could sign up from a state Youro cannot treat in. */}
          <Route path="/signupmyself" element={<Navigate to="/signup" replace />}></Route>
          <Route path="/signupforfamilymember" element={<Navigate to="/signup" replace />}></Route>
          <Route path="/policy" element={<Policy />}></Route>
          <Route path="/telehealth-consent" element={<Telehealth />}></Route>
          <Route path="/terms-conditions" element={<Terms />}></Route>
          <Route path="/notice-of-privacy-practices" element={<NoticeOfPrivacyPractices />}></Route>
          <Route path="/hipaa-authorization" element={<HipaaAuthorization />}></Route>
          <Route path="/about" element={<Aboutus />} />
          <Route path="/pricing" element={<PricingPage />} />

          <Route
            path="/patient-home"
            element={
              <PatientProtectedRoute>
                <PatientView />
              </PatientProtectedRoute>
            }
          ></Route>
          <Route
            path="/doctor-home"
            element={
              <DoctorProtectedRoute>
                <DoctorHomePage />
              </DoctorProtectedRoute>
            }
          ></Route>
          <Route
            path="/doctor-prescriptions"
            element={
              <DoctorProtectedRoute>
                <DoctorPrescriptions />
              </DoctorProtectedRoute>
            }
          ></Route>
          <Route
            path="/help-support"
            element={
              <DoctorProtectedRoute>
                <DoctorHelpSupport />
              </DoctorProtectedRoute>
            }
          ></Route>
          <Route
            path="/admin-doctors"
            element={
              <AdminProtectedRoute>
                <AdminDoctorsList />
              </AdminProtectedRoute>
            }
          ></Route>
          <Route
            path="/admin-patients"
            element={
              <AdminProtectedRoute>
                <AdminPatientList />
              </AdminProtectedRoute>
            }
          ></Route>
          <Route
            path="/doctor-profile"
            element={
              <DoctorProtectedRoute>
                <DoctorProfile />
              </DoctorProtectedRoute>
            }
          ></Route>
          <Route
            path="/doctor-access"
            element={
              <DoctorProtectedRoute>
                <DoctorAccessDenied />
              </DoctorProtectedRoute>
            }
          ></Route>
          <Route
            path="/doctor-appointment"
            element={
              <DoctorProtectedRoute>
                <DoctorAppointments />
              </DoctorProtectedRoute>
            }
          ></Route>
          <Route
            path="/doctor-view-profile/:patientId?/:apptId?"
            element={
              <ClinicianProtectedRoute>
                <ViewProfile />
              </ClinicianProtectedRoute>
            }
          ></Route>
          <Route
            path="/doctor-chat"
            element={
              <DoctorProtectedRoute>
                <DoctorChat />
              </DoctorProtectedRoute>
            }
          ></Route>
          <Route
            path="/admin-denied-doctors"
            element={
              <AdminProtectedRoute>
                <AdminDeniedDoctorsList />
              </AdminProtectedRoute>
            }
          ></Route>
          <Route
            path="/admin-view-doctor"
            element={
              <AdminProtectedRoute>
                <AdminViewDoctorProfile />
              </AdminProtectedRoute>
            }
          ></Route>
          <Route
            path="/admin-view-patient"
            element={
              <AdminProtectedRoute>
                <AdminViewPatientProfile />
              </AdminProtectedRoute>
            }
          ></Route>
          <Route
            path="/manage-approved-medicine"
            element={
              <AdminProtectedRoute>
                <AdminMaintainenceList />
              </AdminProtectedRoute>
            }
          ></Route>
          <Route
            path="/admin-assistants"
            element={
              <AdminProtectedRoute>
                <AdminAssistantList />
              </AdminProtectedRoute>
            }
          ></Route>
          <Route path='/admin-note-template' element={<AdminProtectedRoute><NoteTemplate/></AdminProtectedRoute>}></Route>
          <Route path='/admin-add-template' element={<AdminProtectedRoute><AdminAddTemplate/></AdminProtectedRoute>}></Route>
          <Route path="/no-access" element={<AccessDenied />}></Route>
          {/* From the email sent after the public questionnaire. */}
          <Route path="/confirm-results" element={<ConfirmResults />}></Route>
          <Route path="/waitlist/unsubscribe" element={<WaitlistUnsubscribe />}></Route>
          {/* Last, and matching everything: an unknown address used to render a
              blank page. */}
          <Route path="*" element={<NotFound />}></Route>
        </Routes>
      </Router>
      {/* The only toast container in the app. Screens must not mount their
          own: every mounted container renders every toast, so two on screen
          show each one twice. See src/utils/toast.js. */}
      <YouroToaster />
    </>
  );
}

export default App;
